<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import { RouterLink } from 'vue-router'
import { ArrowTopRightOnSquareIcon } from '@heroicons/vue/20/solid'
import SlidePanel from '@/components/shared/SlidePanel.vue'
import CustomerNotesSection from '@/components/customers/CustomerNotesSection.vue'
import CustomerPitchGrantsSection from '@/components/customers/CustomerPitchGrantsSection.vue'
import CustomerSupportAccessSection from '@/components/customers/CustomerSupportAccessSection.vue'
import {
  usersApi,
  type ActivityPeriod,
  type LightbaseUser,
  type UserActivityResponse,
} from '@/api/users.api'
import { impersonationApi } from '@/api/impersonation.api'
import { supportAccessErrorMessage, type SupportAccessRequest } from '@/api/support-access.api'
import { runtimeConfig } from '@/config/runtimeEnv'
import {
  customerDisplayName,
  dayBarHeight,
  eventTypeClass,
  eventTypeLabel,
  formatDaysUntil,
  formatHours,
  formatRelativeTime,
  formatShortDate,
  statusClass,
  statusLabel,
  urgencyClass,
} from '@/utils/customers'

const props = defineProps<{
  user: LightbaseUser | null
}>()

const emit = defineEmits<{ close: [] }>()

const detailPeriod = ref<ActivityPeriod>('last_7_days')
const detail = ref<UserActivityResponse | null>(null)
const detailLoading = ref(false)
const detailError = ref('')
const impersonating = ref<'customer' | 'workspace' | null>(null)
const impersonateError = ref('')
/**
 * The customer's running email-access grant. "View as" works without one and opens the support view, where the
 * backend hides everything from Gmail/Outlook; with one the session opens in full mode (email included).
 */
const supportGrant = ref<SupportAccessRequest | null>(null)

const LIGHTHOUSE_APP_URL = runtimeConfig.lighthouseAppUrl.replace(/\/$/, '')

const open = computed(() => !!props.user)
const title = computed(() => (props.user ? customerDisplayName(props.user) : ''))

const maxDaySeconds = computed(() =>
  Math.max(0, ...((detail.value?.daily ?? []).map((d) => d.active_seconds))),
)

const periodOptions = [
  { key: 'last_7_days' as const, label: '7 days' },
  { key: 'last_30_days' as const, label: '30 days' },
  { key: 'week' as const, label: 'This week' },
  { key: 'month' as const, label: 'This month' },
]

let loadSeq = 0

function errorMessage(status: number | undefined, data: unknown): string {
  const detail =
    data && typeof data === 'object' && 'detail' in data
      ? (data as { detail: unknown }).detail
      : null
  if (typeof detail === 'string' && detail.trim()) return detail
  if (Array.isArray(detail) && detail.length) {
    const first = detail[0]
    if (typeof first === 'string') return first
    if (first && typeof first === 'object' && 'msg' in first) {
      return String((first as { msg: unknown }).msg)
    }
  }
  if (!status) return 'Failed to load activity (network error)'
  return `Failed to load activity (${status})`
}

async function loadDetail() {
  const user = props.user
  if (!user) {
    detail.value = null
    detailError.value = ''
    detailLoading.value = false
    return
  }

  const seq = ++loadSeq
  detailLoading.value = true
  detailError.value = ''

  try {
    const res = await usersApi.activity(user.id, {
      period: detailPeriod.value,
    })
    if (seq !== loadSeq) return

    if (res.status !== 200 || !res.data) {
      detailError.value = errorMessage(res.status, res.data)
      detail.value = null
      return
    }
    detail.value = res.data
  } catch (e: unknown) {
    if (seq !== loadSeq) return
    detailError.value = e instanceof Error ? e.message : 'Failed to load activity'
    detail.value = null
  } finally {
    if (seq === loadSeq) detailLoading.value = false
  }
}

async function viewAsUser(options: { forceOnboarded: boolean }) {
  const user = props.user
  if (!user) return
  impersonateError.value = ''
  // With an email-access grant the customer already approved a reason, which doubles as the audit reason.
  const reason = supportGrant.value
    ? supportGrant.value.reason ?? undefined
    : window.prompt(`Reason for viewing as ${customerDisplayName(user)}? (optional)`) ?? undefined
  impersonating.value = options.forceOnboarded ? 'workspace' : 'customer'
  try {
    const res = await impersonationApi.start(user.id, reason || undefined)
    if (res.status !== 200 || !res.data?.access_token) {
      impersonateError.value = supportAccessErrorMessage(res.data, 'Failed to start impersonation.')
      return
    }
    const s = res.data
    const returnUrl = `${window.location.origin}/customers`
    const params = new URLSearchParams({
      access_token: s.access_token,
      refresh_token: s.refresh_token,
      session_id: s.session_id,
      email: s.user?.email || user.email || '',
      name: s.user?.name || customerDisplayName(user),
      return: returnUrl,
      // Product app uses this to optionally skip the target's onboarding gate.
      force_onboarded: options.forceOnboarded ? '1' : '0',
      // 'limited' = support view (email hidden by the backend); 'full' = the customer approved email access.
      access_mode: s.access_mode ?? (supportGrant.value ? 'full' : 'limited'),
    })
    window.open(`${LIGHTHOUSE_APP_URL}/impersonate#${params.toString()}`, '_blank')
  } catch {
    impersonateError.value = 'Failed to start impersonation.'
  } finally {
    impersonating.value = null
  }
}

// Remount-friendly: parent should pass :key="user?.id". Single watch avoids the
// period-reset double-fetch race that could leave a stale error on screen.
watch(
  [() => props.user?.id, detailPeriod],
  () => {
    void loadDetail()
  },
  { immediate: true },
)
</script>

<template>
  <SlidePanel :open="open" :title="title" width="max-w-md" @close="emit('close')">
    <template v-if="user">
      <p class="truncate font-mono text-xs text-gray-400">{{ user.email }}</p>
      <p v-if="user.organization_name" class="mt-1 text-sm text-gray-600">
        {{ user.organization_name }}
      </p>

      <div class="mt-4 flex flex-wrap items-center gap-2">
        <span
          class="inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium"
          :class="statusClass(user.subscription_status)"
        >
          {{ statusLabel(user.subscription_status) }}
        </span>
        <span
          v-if="user.next_event"
          class="inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium"
          :class="eventTypeClass(user.next_event.type)"
        >
          {{ eventTypeLabel(user.next_event.type) }}
          ·
          <span :class="urgencyClass(user.next_event.days_until)">
            {{ formatDaysUntil(user.next_event.days_until) }}
          </span>
        </span>
        <span
          v-if="user.at_risk"
          class="inline-flex items-center rounded-full bg-red-50 px-2 py-0.5 text-xs font-medium text-red-700"
        >
          At risk
        </span>
      </div>

      <dl class="mt-5 grid grid-cols-2 gap-3 text-sm">
        <div class="rounded-lg border border-gray-200 p-3">
          <dt class="text-[11px] font-medium uppercase tracking-wide text-gray-400">Last active</dt>
          <dd class="mt-1 font-medium text-gray-900">{{ formatRelativeTime(user.last_active_at) }}</dd>
        </div>
        <div class="rounded-lg border border-gray-200 p-3">
          <dt class="text-[11px] font-medium uppercase tracking-wide text-gray-400">Days inactive</dt>
          <dd class="mt-1 font-medium text-gray-900">
            {{ user.days_inactive == null ? 'Never used' : `${user.days_inactive}d` }}
          </dd>
        </div>
        <div class="rounded-lg border border-gray-200 p-3">
          <dt class="text-[11px] font-medium uppercase tracking-wide text-gray-400">Trial ends</dt>
          <dd class="mt-1 font-medium text-gray-900">{{ formatShortDate(user.trial_end) }}</dd>
        </div>
        <div class="rounded-lg border border-gray-200 p-3">
          <dt class="text-[11px] font-medium uppercase tracking-wide text-gray-400">Period ends</dt>
          <dd class="mt-1 font-medium text-gray-900">{{ formatShortDate(user.current_period_end) }}</dd>
        </div>
      </dl>

      <div
        v-if="user.cancellation_reason || user.cancellation_feedback"
        class="mt-4 rounded-lg border border-orange-100 bg-orange-50/50 p-3"
      >
        <p class="text-[11px] font-medium uppercase tracking-wide text-orange-700">Cancellation notes</p>
        <p v-if="user.cancellation_reason" class="mt-1 text-sm text-gray-800">
          {{ user.cancellation_reason }}
        </p>
        <p v-if="user.cancellation_feedback" class="mt-1 text-sm text-gray-600">
          {{ user.cancellation_feedback }}
        </p>
      </div>

      <div class="mt-5 space-y-2">
        <CustomerSupportAccessSection
          :user-id="user.id"
          :customer-name="customerDisplayName(user)"
          @grant-change="supportGrant = $event"
        />
        <button
          type="button"
          title="Open exactly as the customer sees it, including onboarding"
          class="inline-flex w-full items-center justify-center gap-2 rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
          :disabled="!!impersonating || !LIGHTHOUSE_APP_URL"
          @click="viewAsUser({ forceOnboarded: false })"
        >
          <ArrowTopRightOnSquareIcon class="h-4 w-4" />
          {{ impersonating === 'customer' ? 'Opening…' : 'View as customer' }}
        </button>
        <button
          type="button"
          title="Open the product workspace, skipping onboarding"
          class="inline-flex w-full items-center justify-center gap-2 rounded-lg bg-forest px-3 py-2 text-sm font-medium text-white hover:bg-forest-800 disabled:opacity-50"
          :disabled="!!impersonating || !LIGHTHOUSE_APP_URL"
          @click="viewAsUser({ forceOnboarded: true })"
        >
          <ArrowTopRightOnSquareIcon class="h-4 w-4" />
          {{ impersonating === 'workspace' ? 'Opening…' : 'View workspace' }}
        </button>
        <p class="text-xs text-gray-500">
          {{
            supportGrant
              ? 'Opens with email: the customer approved email access.'
              : 'Opens in support view: email, drafts and the assistant stay hidden.'
          }}
        </p>
        <p v-if="impersonateError" class="text-xs text-red-600">{{ impersonateError }}</p>
        <RouterLink
          v-if="user.organization_id"
          :to="`/accounts/${user.organization_id}`"
          class="button-primary w-full"
        >
          Customer 360
        </RouterLink>
      </div>

      <CustomerPitchGrantsSection :user-id="user.id" />

      <CustomerNotesSection :user-id="user.id" />

      <div class="mt-6 border-t border-gray-100 pt-4">
        <div class="inline-flex rounded-lg border border-gray-200 p-0.5">
          <button
            v-for="opt in periodOptions"
            :key="opt.key"
            type="button"
            class="rounded-md px-2.5 py-1 text-xs font-medium"
            :class="detailPeriod === opt.key ? 'bg-forest text-white' : 'text-gray-600 hover:bg-gray-50'"
            @click="detailPeriod = opt.key"
          >
            {{ opt.label }}
          </button>
        </div>
      </div>

      <div class="mt-4">
        <p v-if="detailLoading" class="py-8 text-center text-sm text-gray-500">Loading activity…</p>
        <p v-else-if="detailError" class="py-8 text-center text-sm text-red-600">{{ detailError }}</p>
        <template v-else-if="detail">
          <div class="grid grid-cols-2 gap-3">
            <div class="rounded-lg border border-gray-200 p-3">
              <p class="text-[11px] font-medium uppercase tracking-wide text-gray-400">Days active</p>
              <p class="mt-1 text-2xl font-semibold text-gray-900">
                {{ detail.days_active }}
                <span class="text-sm font-normal text-gray-400">/ {{ detail.days_in_range }}</span>
              </p>
            </div>
            <div class="rounded-lg border border-gray-200 p-3">
              <p class="text-[11px] font-medium uppercase tracking-wide text-gray-400">Time active</p>
              <p class="mt-1 text-2xl font-semibold text-gray-900">
                {{ formatHours(detail.active_hours) }}
              </p>
            </div>
            <div class="rounded-lg border border-gray-200 p-3">
              <p class="text-[11px] font-medium uppercase tracking-wide text-gray-400">Sessions</p>
              <p class="mt-1 text-2xl font-semibold text-gray-900">{{ detail.session_count }}</p>
            </div>
            <div class="rounded-lg border border-gray-200 p-3">
              <p class="text-[11px] font-medium uppercase tracking-wide text-gray-400">Customer since</p>
              <p class="mt-1 text-sm font-medium text-gray-900">{{ formatShortDate(user.created_at) }}</p>
            </div>
          </div>

          <div class="mt-6">
            <p class="text-xs font-medium uppercase tracking-wide text-gray-400">Daily activity (UTC)</p>
            <div class="mt-3 flex h-28 items-end gap-1">
              <div
                v-for="day in detail.daily"
                :key="day.date"
                class="group relative flex-1 rounded-t"
                :class="day.active ? 'bg-forest/80' : 'bg-gray-100'"
                :style="{ height: dayBarHeight(day.active_seconds, maxDaySeconds) }"
                :title="`${day.date}: ${formatHours(day.active_hours)}`"
              />
            </div>
            <div class="mt-2 flex justify-between text-[10px] text-gray-400">
              <span>{{ detail.daily[0]?.date }}</span>
              <span>{{ detail.daily[detail.daily.length - 1]?.date }}</span>
            </div>
          </div>

          <p class="mt-6 text-xs leading-relaxed text-gray-400">
            Time is estimated from continuous activity sessions (tab heartbeat / API traffic). Gaps
            longer than 5 minutes start a new session.
          </p>
        </template>
      </div>
    </template>
  </SlidePanel>
</template>
